import { Case, Claim, Issue } from "./CaseModel.ts";

/**
 * The claims that go when a party is deleted: those it brings as claimant. A
 * claim where the party is a defendant only loses it as one and stays, even
 * when that leaves it with no defendant, so another can be added.
 */
export function claimsRemovedWithParty(
  claims: Claim[] | undefined,
  partyId: string,
): Claim[] {
  return (claims ?? []).filter((claim) => claim.claimant?.id === partyId);
}

/** The claims where the party is a defendant, of the claim or one of its issues. */
export function claimsAgainstParty(
  claims: Claim[] | undefined,
  partyId: string,
): Claim[] {
  const against = (issue: Issue): boolean =>
    (issue.defendants ?? []).some(({ id }) => id === partyId) ||
    (issue.subIssues ?? []).some(against);
  return (claims ?? []).filter(
    (claim) => against(claim) || (claim.issues ?? []).some(against),
  );
}

// drops the entries of parties that no longer exist; true if any went
const dropDeleted = (
  byParty: { [partyId: string]: unknown } | undefined,
  exists: Set<string>,
): boolean => {
  let dropped = false;
  for (const partyId of Object.keys(byParty ?? {})) {
    if (exists.has(partyId)) continue;
    delete byParty![partyId];
    dropped = true;
  }
  return dropped;
};

/**
 * Clears a case of parties that no longer exist. Deleting a party removes it
 * from `parties` only; this drops the claims it brought (see
 * {@link claimsRemovedWithParty}) and every other reference to it in the
 * claims: as a defendant of a claim or issue, in per-defendant win chances and
 * in the liability shares of claimed amounts. Offers and counterclaims lose
 * their references to the removed claims, as when a claim is deleted directly.
 * The parts of costs towards it go, and its share of the parties'
 * apportionments.
 *
 * Mutates `c`, and returns which fields changed so only those are persisted.
 */
export function pruneDeletedParties(c: Case): {
  claims: boolean;
  settlements: boolean;
  parties: boolean;
  budgets: boolean;
} {
  const exists = new Set((c.parties ?? []).map(({ id }) => id));
  let claims = false;

  const removedClaimIds = new Set<string>();
  const kept = (c.claims ?? []).filter((claim) => {
    // a claim without a claimant is malformed, not orphaned: left alone
    const claimantId = claim.claimant?.id;
    if (claimantId === undefined || exists.has(claimantId)) return true;
    removedClaimIds.add(claim.id);
    return false;
  });
  if (removedClaimIds.size > 0) {
    c.claims = kept;
    claims = true;
  }

  const prune = (issue: Issue) => {
    if (issue.defendants?.some(({ id }) => !exists.has(id))) {
      issue.defendants = issue.defendants.filter(({ id }) => exists.has(id));
      claims = true;
    }
    if (dropDeleted(issue.perDefendantWinChance, exists)) claims = true;
    for (const quantum of issue.quantums ?? [])
      if (dropDeleted(quantum.liabilityShares, exists)) claims = true;
    issue.subIssues?.forEach(prune);
  };
  for (const claim of c.claims ?? []) {
    prune(claim);
    claim.issues?.forEach(prune);
    if (claim.counterToClaim && removedClaimIds.has(claim.counterToClaim)) {
      delete claim.counterToClaim;
      claims = true;
    }
  }

  let settlements = false;
  if (removedClaimIds.size > 0) {
    for (const offer of c.settlements ?? []) {
      const settledClaims = (offer.settledClaims ?? []).filter(
        (id) => !removedClaimIds.has(id),
      );
      if (settledClaims.length === (offer.settledClaims ?? []).length) continue;
      offer.settledClaims = settledClaims;
      settlements = true;
    }
  }

  let parties = false;
  for (const { apportionment } of c.parties ?? [])
    if (dropDeleted(apportionment?.perParty, exists)) parties = true;

  let budgets = false;
  for (const { costs } of c.budgets ?? [])
    for (const { flatFees } of costs)
      for (const { towards } of flatFees)
        if (dropDeleted(towards, exists)) budgets = true;

  return { claims, settlements, parties, budgets };
}
